import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { trackClick } from "../lib/analytics";
import {
  Gauge, Activity, MapPin, Wrench, Newspaper, Wifi, Globe, Search, Plug, Cpu, Network, ArrowRight, Sparkles,
} from "lucide-react";

/**
 * Curated cross-links.
 * Each entry describes a target: which page it links to, why the user should care
 * (contextual "hook"), and an icon.
 * When rendering, we prefer the entries listed for the current page (and skip
 * anything that would link the user back to the page they're already on).
 */

const TARGETS = {
  "/speed-test": { icon: Gauge, title: "Speed test", hook: "See your real Mbps in 15 seconds — with a plain-English verdict." },
  "/down-detector": { icon: Activity, title: "Down Detector", hook: "Is it your Wi-Fi or is Netflix down? Check the live status board." },
  "/my-ip": { icon: MapPin, title: "My IP & location", hook: "See what the internet sees about you — copy your IP in one tap." },
  "/fix-my-internet": { icon: Wrench, title: "Fix my internet", hook: "A calm 7-step checklist for slow connections and dropped calls." },
  "/news": { icon: Newspaper, title: "Outage news", hook: "Fresh reports on ISP outages and slow services — updated hourly." },
  "/tools": { icon: Sparkles, title: "Free network tools", hook: "Six focused utilities — QR, DNS, WHOIS, port and MAC lookup." },
  "/tools/wifi-qr": { icon: Wifi, title: "Wi-Fi QR generator", hook: "One scan gets guests online — no more spelling out passwords." },
  "/tools/dns-lookup": { icon: Globe, title: "DNS lookup", hook: "Resolve any domain's A / MX / TXT / NS in one click." },
  "/tools/whois": { icon: Search, title: "WHOIS lookup", hook: "Find out who owns a domain and when it expires." },
  "/tools/port-check": { icon: Plug, title: "Port checker", hook: "Test if a TCP port is reachable from the public internet." },
  "/tools/mac-lookup": { icon: Cpu, title: "MAC vendor lookup", hook: "Identify unknown devices on your network by MAC." },
  "/tools/subnet-calculator": { icon: Network, title: "Subnet calculator", hook: "Break down IPv4 CIDR blocks instantly." },
};

// Which recommendations to show FIRST for each source path.
// Priority order — good pairings only.
const RECIPES = {
  "/": ["/speed-test", "/down-detector", "/news", "/tools"],
  "/speed-test": ["/fix-my-internet", "/down-detector", "/tools/dns-lookup", "/my-ip"],
  "/down-detector": ["/news", "/speed-test", "/fix-my-internet", "/tools"],
  "/my-ip": ["/speed-test", "/tools/dns-lookup", "/tools/whois", "/down-detector"],
  "/fix-my-internet": ["/speed-test", "/tools/dns-lookup", "/tools/wifi-qr", "/down-detector"],
  "/news": ["/down-detector", "/speed-test", "/tools", "/fix-my-internet"],
  "/tools": ["/speed-test", "/down-detector", "/news", "/fix-my-internet"],
  "/tools/wifi-qr": ["/speed-test", "/tools/subnet-calculator", "/tools/port-check", "/fix-my-internet"],
  "/tools/dns-lookup": ["/tools/whois", "/fix-my-internet", "/speed-test", "/tools/port-check"],
  "/tools/whois": ["/tools/dns-lookup", "/my-ip", "/tools/port-check", "/speed-test"],
  "/tools/port-check": ["/tools/dns-lookup", "/my-ip", "/tools/subnet-calculator", "/speed-test"],
  "/tools/mac-lookup": ["/tools/subnet-calculator", "/my-ip", "/tools/wifi-qr", "/speed-test"],
  "/tools/subnet-calculator": ["/tools/mac-lookup", "/tools/port-check", "/tools/dns-lookup", "/my-ip"],
};

export function CrossLinks({ testid = "cross-links", title = "You might also like" }) {
  const location = useLocation();
  const path = location.pathname.replace(/\/$/, "") || "/";

  const items = useMemo(() => {
    // Match either exact path or dynamic /news/<slug>
    const base = path.startsWith("/news/") && path !== "/news" ? "/news" : path;
    const recipe = RECIPES[base] || ["/speed-test", "/down-detector", "/tools", "/news"];
    return recipe
      .filter((p) => p !== base)
      .map((p) => ({ to: p, ...TARGETS[p] }))
      .filter((x) => x.title)
      .slice(0, 4);
  }, [path]);

  if (!items.length) return null;

  return (
    <section className="mt-10 sm:mt-12" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-3.5 h-3.5 text-[var(--cobalt-bright)]" />
        <h3 className="font-display text-sm sm:text-base font-semibold">{title}</h3>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {items.map((it) => (
          <Link
            key={it.to}
            to={it.to}
            onClick={() => trackClick("cross-link", { to: it.to, from: path })}
            className="glass rounded-xl p-4 lift group flex items-start gap-3"
            data-testid={`cross-link-${it.to.replace(/\//g, "-")}`}
          >
            <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[rgba(61,107,255,0.12)] border border-[rgba(61,107,255,0.28)] shrink-0">
              <it.icon className="w-4 h-4 text-[var(--cobalt-bright)]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-sm font-semibold flex items-center gap-1">
                {it.title}
                <ArrowRight className="w-3 h-3 opacity-0 -ml-1 group-hover:opacity-100 group-hover:ml-0 transition-all" />
              </div>
              <div className="text-xs text-[var(--text-dim)] leading-relaxed mt-1">{it.hook}</div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
